import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { MaterialGroup, MaterialGroupDataService } from '@lib/material-group';
import { BaseSiteLayoutModule } from '@lib/util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { PurchasingGroupAssignment } from '../../models/purchasing-group-assignment.model';
import { PurchasingGroupResponsibilityOccupation } from '../../models/purchasing-group-responsibility-occupation.model';
import { PurchasingGroupDataService } from '../../services/purchasing-group-data.service';
import {
    IOccupationOwner,
    IMaterialGroupItem,
    buildMaterialGroupItems,
} from './purchasing-group-material-groups-dialog.utils';

export class PurchasingGroupMaterialGroupsDialogData {
    constructor(
        public readonly purchasingGroupId: string,
        public readonly companyCodeId: string,
        public readonly companyCode: string,
        public readonly companyCodeName: string | null,
        public readonly assignmentsForCompanyCode: ReadonlyArray<PurchasingGroupAssignment>,
        /**
         * When true the dialog runs in remove-only mode: the company code is no longer on the header level,
         * so adding new responsibilities would be rejected by the domain. Only the existing
         * assignments of this purchasing group are shown — they can be unchecked to remove them.
         */
        public readonly isOrphan = false,
    ) {}
}

export class PurchasingGroupMaterialGroupsDialogResult {
    constructor(public readonly updated: boolean) {}
}

@Component({
    selector: 'lib-purchasing-group-material-groups-dialog',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatDialogModule, BaseSiteLayoutModule, TranslateModule],
    templateUrl: './purchasing-group-material-groups-dialog.component.html',
    styleUrls: ['./purchasing-group-material-groups-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasingGroupMaterialGroupsDialogComponent implements OnInit {
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly materialGroupDataService = inject(MaterialGroupDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly translateService = inject(TranslateService);
    private readonly dialogRef = inject<
        MatDialogRef<PurchasingGroupMaterialGroupsDialogComponent, PurchasingGroupMaterialGroupsDialogResult>
    >(MatDialogRef);
    private readonly data = inject<PurchasingGroupMaterialGroupsDialogData>(MAT_DIALOG_DATA);

    public readonly icon = 'fas fa-sitemap';
    public readonly companyCode = this.data.companyCode;
    public readonly companyCodeName = this.data.companyCodeName;
    public readonly companyCodeLabel = this.companyCodeName
        ? `${this.companyCode} – ${this.companyCodeName}`
        : this.companyCode;
    // marker() ensures the i18n extractor picks up the heading prefix; the template translates it
    // reactively via the translate pipe so a mid-session language switch updates the title.
    public readonly headingPrefix = marker('Warengruppen');
    public readonly isOrphan = this.data.isOrphan;

    public readonly loading = signal(false);
    public readonly saving = signal(false);
    /**
     * Inline error rendered in the dialog body. Unlike the toast notification — which is forwarded
     * to the parent shell via postMessage when the app runs inside the legacy iframe and may never
     * surface there — this banner always renders in the dialog's own DOM, so a save conflict is
     * never silent.
     */
    public readonly saveError = signal<string | null>(null);
    public readonly filterControl = new FormControl<string>('', { nonNullable: true });
    private readonly filterValue = toSignal(this.filterControl.valueChanges, { initialValue: '' });

    private readonly allMaterialGroups = signal<ReadonlyArray<MaterialGroup>>([]);
    private readonly currentAssignments = signal<ReadonlyArray<PurchasingGroupAssignment>>(
        this.data.assignmentsForCompanyCode,
    );
    private readonly occupationsByMaterialGroupId = signal<ReadonlyMap<string, IOccupationOwner>>(new Map());

    private readonly ownMaterialGroupIds = computed<ReadonlySet<string>>(
        () => new Set(this.currentAssignments().map((a) => a.materialGroupId)),
    );

    /** Materialgroup-id → assignment-id, needed to translate "uncheck" actions into bulkRemove calls. */
    private readonly assignmentIdByMaterialGroupId = computed<ReadonlyMap<string, string>>(
        () => new Map(this.currentAssignments().map((a) => [a.materialGroupId, a.id])),
    );

    public readonly selectedMaterialGroupIds = signal<ReadonlySet<string>>(new Set());
    private initialSelectedMaterialGroupIds: ReadonlySet<string> = new Set();

    public readonly items = computed<ReadonlyArray<IMaterialGroupItem<MaterialGroup>>>(() => {
        const all = this.isOrphan
            ? this.allMaterialGroups().filter((mg) => this.ownMaterialGroupIds().has(mg.id))
            : this.allMaterialGroups();
        return buildMaterialGroupItems(
            all,
            this.ownMaterialGroupIds(),
            this.occupationsByMaterialGroupId(),
            this.filterValue(),
        );
    });

    public readonly addIds = computed<ReadonlyArray<string>>(() => {
        const initial = this.initialSelectedMaterialGroupIds;
        return [...this.selectedMaterialGroupIds()].filter((id) => !initial.has(id));
    });

    public readonly removeIds = computed<ReadonlyArray<string>>(() => {
        const selected = this.selectedMaterialGroupIds();
        return [...this.initialSelectedMaterialGroupIds].filter((id) => !selected.has(id));
    });

    public readonly hasChanges = computed(() => this.addIds().length > 0 || this.removeIds().length > 0);
    public readonly canSave = computed(() => !this.saving() && this.hasChanges());

    private readonly visibleFreeMaterialGroupIds = computed<ReadonlyArray<string>>(() =>
        this.items()
            .filter((item) => item.status === 'free')
            .map((item) => item.materialGroup.id),
    );

    public readonly canSelectAllVisibleFree = computed(() => {
        if (this.saving()) return false;
        const visible = this.visibleFreeMaterialGroupIds();
        if (visible.length === 0) return false;
        const selected = this.selectedMaterialGroupIds();
        return visible.some((id) => !selected.has(id));
    });

    private updated = false;

    public ngOnInit(): void {
        this.resetSelection();
        this.loadInitialData();
    }

    public isSelected(materialGroupId: string): boolean {
        return this.selectedMaterialGroupIds().has(materialGroupId);
    }

    public selectAllVisibleFree(): void {
        if (!this.canSelectAllVisibleFree()) return;
        this.saveError.set(null);
        const visible = this.visibleFreeMaterialGroupIds();
        this.selectedMaterialGroupIds.update((current) => {
            const next = new Set(current);
            for (const id of visible) next.add(id);
            return next;
        });
    }

    public toggle(item: IMaterialGroupItem<MaterialGroup>): void {
        if (item.status === 'blocked' || this.saving()) return;
        this.saveError.set(null);
        this.selectedMaterialGroupIds.update((current) => {
            const next = new Set(current);
            if (next.has(item.materialGroup.id)) {
                next.delete(item.materialGroup.id);
            } else {
                next.add(item.materialGroup.id);
            }
            return next;
        });
    }

    public async save(): Promise<void> {
        if (!this.canSave()) return;
        this.saveError.set(null);
        const addIds = this.addIds();
        const removeIds = this.removeIds();
        const addCodes = this.toMaterialGroupCodes(addIds);
        const removeAssignmentIds = this.toAssignmentIds(removeIds);

        this.saving.set(true);
        try {
            if (addCodes.length > 0) {
                await firstValueFrom(
                    this.dataService.bulkAssignAssignments(
                        this.data.purchasingGroupId,
                        [this.data.companyCodeId],
                        addCodes,
                    ),
                );
            }
            if (removeAssignmentIds.length > 0) {
                await firstValueFrom(
                    this.dataService.bulkRemoveAssignments(this.data.purchasingGroupId, removeAssignmentIds),
                );
            }
            this.updated = true;
            this.notificationService.showNotification(
                marker('Zuordnungen wurden gespeichert.'),
                NotificationMessageLevel.SUCCESS,
            );
            this.saving.set(false);
            this.close();
            return;
        } catch (error) {
            await this.handleSaveError(error);
        } finally {
            this.saving.set(false);
        }
    }

    public close(): void {
        this.dialogRef.close(new PurchasingGroupMaterialGroupsDialogResult(this.updated));
    }

    /**
     * Builds a parameter-substituted message. translateService.instant() only interpolates the
     * params when the key already exists in the loaded translation file; for a freshly added marker
     * whose key has not been extracted into the i18n files yet it returns the raw key with literal
     * {{placeholders}}. Running the returned template through the parser substitutes the params in
     * every case — both before and after the keys are extracted for the release.
     */
    private interpolate(key: string, params: Record<string, string>): string {
        const template = this.translateService.instant(key);
        return this.translateService.parser.interpolate(template, params) ?? template;
    }

    private toAssignmentIds(materialGroupIds: ReadonlyArray<string>): ReadonlyArray<string> {
        const lookup = this.assignmentIdByMaterialGroupId();
        return materialGroupIds
            .map((id) => lookup.get(id))
            .filter((id): id is string => typeof id === 'string');
    }

    /**
     * Translates the id-keyed selection state into the material group business codes the assignment
     * API now expects (EPROC-11000). The selection is still tracked by the immutable id internally;
     * only the request key is the code.
     */
    private toMaterialGroupCodes(materialGroupIds: ReadonlyArray<string>): ReadonlyArray<string> {
        const codeById = new Map(this.allMaterialGroups().map((mg) => [mg.id, mg.code]));
        return materialGroupIds
            .map((id) => codeById.get(id))
            .filter((code): code is string => typeof code === 'string');
    }

    private async handleSaveError(error: unknown): Promise<void> {
        if (this.dataService.isAssignmentConflict(error)) {
            const conflict = this.dataService.getAssignmentConflict(error);
            const message = conflict
                ? this.interpolate(
                      marker('Warengruppe {{wg}} ist bereits durch {{code}} – {{name}} belegt.'),
                      {
                          wg: conflict.materialGroupCode,
                          code: conflict.assignedPurchasingGroupCode,
                          name: conflict.assignedPurchasingGroupName,
                      },
                  )
                : this.translateService.instant(marker('Eine Zuordnung wurde zwischenzeitlich vergeben.'));
            this.saveError.set(message);
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            await this.reload();
            return;
        }
        const message = this.dataService.resolveErrorMessage(
            error,
            marker('Zuordnungen konnten nicht gespeichert werden.'),
        );
        this.saveError.set(message);
        this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
    }

    private async reload(): Promise<void> {
        try {
            const requests = {
                assignments: this.dataService.getAssignments(this.data.purchasingGroupId),
                ...(this.isOrphan
                    ? {}
                    : { occupations: this.dataService.getResponsibilityOccupations([this.data.companyCodeId], true) }),
            };
            const result = await firstValueFrom(forkJoin(requests));
            const forThisCompanyCode = result.assignments.filter((a) => a.companyCodeId === this.data.companyCodeId);
            this.currentAssignments.set(forThisCompanyCode);
            this.occupationsByMaterialGroupId.set(this.buildOccupationsMap(result.occupations ?? []));
            this.resetSelection();
        } catch (error) {
            const message = this.dataService.resolveErrorMessage(
                error,
                marker('Zuordnungen konnten nicht neu geladen werden.'),
            );
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
        }
    }

    private loadInitialData(): void {
        this.loading.set(true);
        const requests = {
            materialGroups: this.materialGroupDataService.getAllMaterialGroups(),
            ...(this.isOrphan
                ? {}
                : { occupations: this.dataService.getResponsibilityOccupations([this.data.companyCodeId]) }),
        };
        forkJoin(requests).subscribe({
            next: ({ materialGroups, occupations }) => {
                this.allMaterialGroups.set([...materialGroups]);
                this.occupationsByMaterialGroupId.set(this.buildOccupationsMap(occupations ?? []));
                this.loading.set(false);
            },
            error: (error) => {
                this.loading.set(false);
                const message = this.dataService.resolveErrorMessage(
                    error,
                    marker('Warengruppen konnten nicht geladen werden.'),
                );
                this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            },
        });
    }

    private buildOccupationsMap(
        occupations: ReadonlyArray<PurchasingGroupResponsibilityOccupation>,
    ): ReadonlyMap<string, IOccupationOwner> {
        const map = new Map<string, IOccupationOwner>();
        for (const occ of occupations) {
            if (occ.ownerPurchasingGroupId === this.data.purchasingGroupId) continue;
            map.set(occ.materialGroupId, {
                code: occ.ownerPurchasingGroupCode,
                name: occ.ownerPurchasingGroupName,
            });
        }
        return map;
    }

    private resetSelection(): void {
        const initial = new Set(this.currentAssignments().map((a) => a.materialGroupId));
        this.initialSelectedMaterialGroupIds = initial;
        this.selectedMaterialGroupIds.set(new Set(initial));
    }
}
